import { logger } from '@talos/kyoko';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface BlotterSettings {
  showOnlySelfOrdersAndTrades: boolean;
}

interface BlotterSettingsContextValue extends BlotterSettings {
  setShowOnlySelfOrdersAndTrades: (value: boolean) => void;
}

const BlotterSettingsContext = createContext<BlotterSettingsContextValue | undefined>(undefined);

const STORAGE_KEY = 'blotter-settings';

function getStoredSettings(): Partial<BlotterSettings> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function storeSettings(settings: BlotterSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    logger.warn(`Failed to store blotter settings: ${JSON.stringify(settings)}`);
  }
}

interface BlotterSettingsProviderProps {
  children: ReactNode;
}

export function BlotterSettingsProvider({ children }: BlotterSettingsProviderProps) {
  const stored = getStoredSettings();

  // Initialize with stored values or defaults based on config
  const [settings, setSettings] = useState<BlotterSettings>({
    showOnlySelfOrdersAndTrades: stored.showOnlySelfOrdersAndTrades ?? false,
  });

  // Persist changes to localStorage
  useEffect(() => {
    storeSettings(settings);
  }, [settings]);

  const setShowOnlySelfOrdersAndTrades = useCallback((value: boolean) => {
    setSettings(prev => ({ ...prev, showOnlySelfOrdersAndTrades: value }));
  }, []);

  return (
    <BlotterSettingsContext.Provider
      value={{
        ...settings,
        setShowOnlySelfOrdersAndTrades,
      }}
    >
      {children}
    </BlotterSettingsContext.Provider>
  );
}

export function useBlotterSettings(): BlotterSettingsContextValue {
  const context = useContext(BlotterSettingsContext);
  if (!context) {
    throw new Error('useBlotterSettings must be used within BlotterSettingsProvider');
  }
  return context;
}
